import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  LoadingButton,
  PageListLayout,
  ServerPaginatedtable,
  usePermission,
} from "../../../shared";
import { Add, Clear } from "@mui/icons-material";
import ImportIcon from "../../../assets/images/Icons/ic_import.svg";
import SaveIcon from "../../../assets/images/Icons/ic_save.svg";
import { useMemo } from "react";
import { DeleteFutureCostDialog, FilterSection } from "../components";
import { useFutureCostList } from "../hooks";
import { useNavigate } from "react-router-dom";
import editIcon from "../../../assets/images/Icons/editIcon.png";
import { ImportFutureCostsDialog } from "../components/ImportFutureCostsDialog";
import dayjs from "dayjs";
import { formatCost } from "../../../utils";

// Default column configuration for editable cells
const defaultColumn = {
  cell: ({ getValue, row: { index, original }, column: { id }, table }) => {
    const initialValue = getValue();
    const { editRow, getEditRow } = table.options.meta;
    const isEditing = editRow.id === original.id;
    return isEditing
      ? getEditRow({ row: editRow, columnId: id, tableRow: original })
      : id === "costing_date"
        ? dayjs(initialValue).format("DD MMM YYYY")
        : `$${formatCost(initialValue)}`;
  },
};

export const FutureCostList = () => {
  const navigate = useNavigate();
  const {
    state,
    listQuery,
    tableState,
    moduleName,
    isUpdating,
    tableHelpers,
    isDownloading,
    importTemplateQuery,
    deleteFutureCostQuery,
    getEditRow,
    dispatch,
    resetEdit,
    onBulkDelete,
    onSaveChanges,
    onFilterChange,
    onImportFutureCosts,
    downloadTemplateAsync,
  } = useFutureCostList();

  const deletable = usePermission("future-costing-delete");
  const creatable = usePermission("future-costing-create");
  const editable = usePermission("future-costing-modify");
  const importable = usePermission("future-costing-import");

  // Extract pagination info from the list query
  const apiPagination = listQuery.data?.pagination ?? {
    total: 0,
    lastPage: 1,
  };

  // Define breadcrumbs for navigation
  const breadcrumbs = [
    <Typography
      key="2"
      color="inherit"
      sx={{ fontSize: "14px", fontWeight: 400 }}
    >
      Future Costing - Ingredients
    </Typography>,
  ];

  // Define table columns
  const columns = useMemo(() => {
    return [
      {
        id: "select",
        hidden: !deletable,
        header: ({ table }) => (
          <Checkbox
            size="small"
            sx={{ padding: 0 }}
            checked={table.getIsAllRowsSelected()}
            indeterminate={table.getIsSomeRowsSelected()}
            onChange={table.getToggleAllRowsSelectedHandler()}
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            size="small"
            sx={{ padding: 0 }}
            checked={row.getIsSelected()}
            disabled={!row.getCanSelect()}
            indeterminate={row.getIsSomeSelected()}
            onChange={row.getToggleSelectedHandler()}
          />
        ),
      },
      {
        header: "Date",
        accessorKey: "costing_date",
        meta: {
          width: 120,
        },
      },
      {
        header: "Ingredient Name",
        accessorKey: "ingredient_name",
        cell: ({ getValue, row: { original } }) => (
          <Typography
            onClick={() =>
              navigate(`/ingredients/details/${original?.ingredient_id}`)
            }
            variant="subtitle2"
            sx={{
              textDecoration: "underline",
              cursor: "pointer",
              color: "neutral.10",
            }}
          >
            {getValue()}
          </Typography>
        ),
      },
      {
        header: "Status",
        accessorKey: "status",
        cell: ({ getValue }) => {
          const status = getValue();

          return status ? (
            <Chip
              size="small"
              color="primary"
              variant="filled"
              label="ACTIVE"
            />
          ) : (
            <Chip size="small" color="gray" label="INACTIVE" />
          );
        },
      },
      {
        header: "Ingredient Type",
        accessorKey: "ingredient_type",
        cell: ({ getValue }) => getValue(),
      },
      {
        header: "Tax Group",
        accessorKey: "tax_group_name",
        cell: ({ getValue }) => getValue(),
      },
      {
        header: "Buying Unit",
        accessorKey: "buying_unit",
        cell: ({ getValue }) => getValue(),
      },
      {
        header: "Cur Cost ex gst($)",
        accessorKey: "current_cost_exc_gst",
        cell: ({ getValue }) => `$${formatCost(getValue())}`,
      },
      {
        header: "Cur Cost in gst($)",
        accessorKey: "current_cost_inc_gst",
        cell: ({ getValue }) => `$${formatCost(getValue())}`,
      },
      {
        header: "Future Cost ex gst($)",
        accessorKey: "future_cost_exc_gst",
      },
      {
        header: "Future Cost in gst($)",
        accessorKey: "future_cost_inc_gst",
      },
      {
        id: "action",
        hidden: !editable,
        cell: ({ row }) =>
          row.original.id === state.formData.id ? (
            <Stack flexDirection="row">
              <Tooltip title="Save" arrow disableInteractive>
                <IconButton
                  size="small"
                  onClick={(event) => onSaveChanges(event, row.original)}
                  disabled={isUpdating}
                >
                  {isUpdating ? (
                    <CircularProgress size={20} color="primary" />
                  ) : (
                    <img src={SaveIcon} alt="save-icon" />
                  )}
                </IconButton>
              </Tooltip>
              <Tooltip title="Close" arrow disableInteractive>
                <IconButton size="small" onClick={resetEdit}>
                  <Clear color="error" />
                </IconButton>
              </Tooltip>
            </Stack>
          ) : (
            <IconButton
              size="small"
              onClick={() =>
                dispatch({
                  type: "SET_EDIT_ROW",
                  payload: row.original,
                })
              }
            >
              <Box component="img" src={editIcon} height={20} width={20} />
            </IconButton>
          ),
      },
    ];
  }, [state.formData, isUpdating]);

  return (
    <PageListLayout breadcrumbs={breadcrumbs} sx={{ p: 0 }}>
      <ServerPaginatedtable
        key={state.resetRowSelection}
        headerText="Future Costing - Ingredients"
        columns={columns}
        isLoading={listQuery.isLoading}
        defaultData={listQuery?.data?.future_costings || []}
        pagination={tableState.pagination}
        setPagination={(callback) =>
          tableHelpers.setPagination({ tableName: moduleName, callback })
        }
        paginationProps={apiPagination}
        rightContent={
          <Stack flexDirection="row" gap="8px">
            {importable && (
              <Button
                variant="outlined"
                size="small"
                color="gray2"
                startIcon={
                  <Box
                    component="img"
                    src={ImportIcon}
                    alt="import-image"
                  ></Box>
                }
                sx={{ width: "96px" }}
                onClick={(e) =>
                  dispatch({
                    type: "SET_DIALOG",
                    payload: { name: "import", value: true },
                  })
                }
              >
                Import
              </Button>
            )}
            {creatable && (
              <Button
                variant="contained"
                size="small"
                startIcon={<Add />}
                onClick={() => navigate("new")}
              >
                Add New
              </Button>
            )}
          </Stack>
        }
        tableHeaderContent={
          <Stack flexDirection="row" justifyContent="space-between" gap="8px">
            <FilterSection
              moduleName={moduleName}
              onChange={onFilterChange}
              state={state.filter}
            />
            <LoadingButton
              size="small"
              variant="contained"
              color="error"
              onClick={(e) =>
                dispatch({
                  type: "SET_DIALOG",
                  payload: { name: "delete", value: true },
                })
              }
              disabled={state.selectedRows.length === 0}
              loading={deleteFutureCostQuery.isPending}
            >
              Delete
            </LoadingButton>
          </Stack>
        }
        search={tableState.search}
        onSearchChange={tableHelpers.onSearchHandler}
        sorting={tableState.sorting}
        setSorting={(callback) =>
          tableHelpers.setSorting({ tableName: moduleName, callback })
        }
        resetRowSelection={state.resetRowSelection}
        onChangeRowSelection={(selectedRows) => {
          dispatch({ type: "SET_SELECTED_ROWS", payload: selectedRows });
        }}
        editRow={state.formData}
        getEditRow={getEditRow}
        defaultColumn={defaultColumn}
      />

      {
        /* Delete Future Cost Dialog*/
        state.dialog.delete && (
          <DeleteFutureCostDialog
            onDelete={onBulkDelete}
            open={state.dialog.delete}
            rows={state.selectedRows}
            onCancel={() =>
              dispatch({
                type: "SET_DIALOG",
                payload: { name: "delete", value: false },
              })
            }
          />
        )
      }

      {deletable && (
        <ImportFutureCostsDialog
          importQuery={importTemplateQuery}
          isDownloading={isDownloading}
          open={state.dialog.import}
          onDownLoadTemplate={downloadTemplateAsync}
          onImport={onImportFutureCosts}
          onCancel={(e) =>
            dispatch({
              type: "SET_DIALOG",
              payload: { name: "import", value: false },
            })
          }
        />
      )}
    </PageListLayout>
  );
};
